const getNestedPayloadObjects = (value, seen = new Set()) => {
    if (!value || typeof value !== "object" || seen.has(value)) return [];

    seen.add(value);

    const objects = [value];
    Object.values(value).forEach((child) => {
        if (child && typeof child === "object") {
            objects.push(...getNestedPayloadObjects(child, seen));
        }
    });

    return objects;
};

const getResponseObjects = (response) => [
    response,
    ...getNestedPayloadObjects(response?.result),
    ...getNestedPayloadObjects(response?.data),
].filter(Boolean);

const getBooleanField = (object, camelCaseName, pascalCaseName) =>
    object?.[camelCaseName] ?? object?.[pascalCaseName];

export const getEmployeeId = (employee) =>
    employee?.employeeId ?? employee?.id ?? null;

export const normalizeEmployeeId = (employeeId) => {
    if (employeeId === null || employeeId === undefined || employeeId === "") {
        return null;
    }

    return String(employeeId);
};

const getStep = (step) => step?.clientServiceSubscriptionTask ?? step;

export const getStrictlyLaterStep = (steps, currentStep) => {
    const currentOrder = Number(getStep(currentStep)?.order);

    if (!Array.isArray(steps) || !Number.isFinite(currentOrder)) return null;

    return steps.reduce((nextStep, candidate) => {
        const candidateOrder = Number(getStep(candidate)?.order);
        const nextOrder = Number(getStep(nextStep)?.order);

        if (!Number.isFinite(candidateOrder) || candidateOrder <= currentOrder) {
            return nextStep;
        }

        return nextStep === null || candidateOrder < nextOrder
            ? candidate
            : nextStep;
    }, null);
};

export const didMutationSucceed = (response) => {
    if (!response || typeof response !== "object") return false;

    if (response.success === false || response?.data?.success === false) {
        return false;
    }

    return response.success === true || response?.data?.success === true;
};

export const didTransitionToComplete = (response) => {
    if (!didMutationSucceed(response)) return false;

    return getResponseObjects(response).some(
        (object) =>
            getBooleanField(
                object,
                "transitionedToComplete",
                "TransitionedToComplete",
            ) === true,
    );
};

export const shouldCelebrateStepCompletion = (
    response,
    completedStep,
    nextStep,
) => {
    if (!didTransitionToComplete(response)) return false;

    const completedStepOrder = Number(getStep(completedStep)?.order);
    const nextStepOrder = Number(getStep(nextStep)?.order);
    if (
        !Number.isFinite(completedStepOrder) ||
        !Number.isFinite(nextStepOrder) ||
        nextStepOrder <= completedStepOrder
    ) {
        return false;
    }

    const completedEmployeeId = normalizeEmployeeId(
        getEmployeeId(getStep(completedStep)?.completedBy),
    );
    const nextStepAssignedEmployeeId = normalizeEmployeeId(
        getEmployeeId(getStep(nextStep)?.assignedEmployees?.[0]),
    );

    if (completedEmployeeId === null || nextStepAssignedEmployeeId === null) {
        return false;
    }

    return completedEmployeeId !== nextStepAssignedEmployeeId;
};

export const shouldCelebrateServiceCompletion = (response) =>
    didTransitionToComplete(response);
